import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import {
  IconClipboardCheck,
  IconAlertTriangle,
  IconTruck,
  IconClock,
  IconCheck,
  IconX,
  IconArrowRight,
  IconCarCrash,
  IconDroplet,
  IconShieldCheck,
} from '@tabler/icons-react';

export default function ManagerDashboard() {
  const { userProfile, org, orgId } = useAuth();
  const navigate = useNavigate();
  const [checks, setChecks] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [assets, setAssets] = useState([]);
  const [tanks, setTanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const branchId = userProfile?.branchId;

  useEffect(() => {
    const load = async () => {
      if (!orgId || !branchId) {
        setLoading(false);
        return;
      }
      try {
        const [checksSnap, assetsSnap, incidentsSnap, tanksSnap] = await Promise.all([
          getDocs(query(
            collection(db, 'organisations', orgId, 'branches', branchId, 'checkRecords'),
            orderBy('submittedAt', 'desc'),
            limit(6)
          )),
          getDocs(collection(db, 'organisations', orgId, 'branches', branchId, 'assets')),
          getDocs(query(
            collection(db, 'organisations', orgId, 'incidents'),
            orderBy('detectedAt', 'desc'),
            limit(4)
          )),
          getDocs(collection(db, 'organisations', orgId, 'branches', branchId, 'dervTanks')),
        ]);

        setChecks(checksSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAssets(assetsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setIncidents(incidentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setTanks(tanksSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    load();
  }, [orgId, branchId]);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
  const brandColour = org?.brandColour || 'var(--color-brand)';

  const blockedAssets = assets.filter(a => a.isBlocked);
  const todayChecks = checks.filter(c => {
    if (!c.submittedAt) return false;
    const date = c.submittedAt.toDate ? c.submittedAt.toDate() : new Date(c.submittedAt);
    return date.toDateString() === new Date().toDateString();
  });
  const openIncidents = incidents.filter(i => i.status === 'open');

  const branchName = branchId
    ? branchId.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
    : 'Branch';

  const formatTime = (ts) => {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (date.toDateString() === today.toDateString()) {
      return `Today ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return `Yesterday ${date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '?';

  const avatarColours = [
    { bg: '#E6F1FB', color: '#185FA5' },
    { bg: '#EAF3DE', color: '#3B6D11' },
    { bg: '#EEEDFE', color: '#534AB7' },
    { bg: '#FCEBEB', color: '#A32D2D' },
    { bg: '#FDF0E8', color: '#993C1D' },
    { bg: '#E1F5EE', color: '#085041' },
  ];

  const stats = [
    { label: 'Checks done today', value: todayChecks.length, colour: 'var(--color-success)', icon: IconClipboardCheck, path: '/checks' },
    { label: 'Failed today', value: todayChecks.filter(c => c.result === 'fail').length, colour: todayChecks.filter(c => c.result === 'fail').length > 0 ? 'var(--color-danger)' : 'var(--color-success)', icon: IconClock, path: '/checks' },
    { label: 'Open incidents', value: openIncidents.length, colour: openIncidents.length > 0 ? 'var(--color-danger)' : 'var(--color-success)', icon: IconCarCrash, path: '/incidents' },
    { label: 'Assets blocked', value: blockedAssets.length, colour: blockedAssets.length > 0 ? 'var(--color-danger)' : 'var(--color-success)', icon: IconTruck, path: '/assets' },
  ];

  const mainTank = tanks[0];
  const tankPct = mainTank ? Math.round((mainTank.currentLitres / mainTank.capacityLitres) * 100) : null;
  const tankColour = tankPct <= 15 ? 'var(--color-danger)' : tankPct <= 30 ? 'var(--color-warning)' : 'var(--color-success)';

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)', minHeight: '100vh' }}>

        {/* Header */}
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontSize: 22, fontWeight: 400, color: 'var(--color-text-primary)', marginBottom: 4 }}>
            {greeting}, {userProfile?.name?.split(' ')[0] || 'there'}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-tertiary)' }}>{dateStr}</p>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            background: brandColour + '18',
            color: brandColour,
            borderRadius: 'var(--radius-full)',
            padding: '3px 10px',
            marginTop: 8,
          }}>
            <IconShieldCheck size={12} stroke={1.5} />
            {branchName} · Manager
          </div>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 12, marginBottom: 20 }}>
          {stats.map(stat => {
            const Icon = stat.icon;
            return (
              <div key={stat.label} onClick={() => navigate(stat.path)} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '18px 20px', cursor: 'pointer', transition: 'border-color 0.15s' }}
                onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--color-border-medium)'}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--color-border-light)'}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div style={{ fontSize: 36, fontWeight: 400, color: stat.colour, lineHeight: 1 }}>{stat.value}</div>
                  <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: stat.colour + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon size={16} color={stat.colour} stroke={1.5} />
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{stat.label}</div>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 16 }}>

          {/* Left column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Recent checks */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <div style={{ fontSize: 14, fontWeight: 400 }}>Recent checks</div>
                <span onClick={() => navigate('/checks')} style={{ fontSize: 12, color: brandColour, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                  View all <IconArrowRight size={13} stroke={1.5} />
                </span>
              </div>
              {loading ? (
                <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
              ) : checks.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>
                  No checks recorded yet
                </div>
              ) : checks.map((check, i) => {
                const colours = avatarColours[i % avatarColours.length];
                return (
                  <div key={check.id} onClick={() => navigate(`/checks/${check.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: i < checks.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, color: colours.color, flexShrink: 0 }}>
                      {getInitials(check.submittedByName)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 2 }}>{check.submittedByName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                        {check.assetReg && <span style={{ fontFamily: 'monospace', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3 }}>{check.assetReg}</span>}
                        {check.assetName}
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{formatTime(check.submittedAt)}</div>
                    <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, flexShrink: 0, background: check.result === 'pass' ? 'var(--color-success-light)' : check.result === 'fail' ? 'var(--color-danger-light)' : 'var(--color-warning-light)', color: check.result === 'pass' ? 'var(--color-success)' : check.result === 'fail' ? 'var(--color-danger)' : 'var(--color-warning)' }}>
                      {check.result === 'pass' ? 'Pass' : check.result === 'fail' ? 'Fail' : 'Issues'}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Assets */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <div style={{ fontSize: 14, fontWeight: 400 }}>Assets</div>
                <span onClick={() => navigate('/assets')} style={{ fontSize: 12, color: brandColour, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                  View all <IconArrowRight size={13} stroke={1.5} />
                </span>
              </div>
              {loading ? (
                <div style={{ padding: 20, textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>Loading...</div>
              ) : assets.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>No assets yet</div>
              ) : assets.slice(0, 5).map((asset, i) => (
                <div key={asset.id} onClick={() => navigate(`/assets/${asset.id}`)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderBottom: i < Math.min(assets.length, 5) - 1 ? '0.5px solid var(--color-border-light)' : 'none', background: asset.isBlocked ? '#FCEBEB' : 'transparent', cursor: 'pointer' }}
                  onMouseEnter={e => !asset.isBlocked && (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
                  onMouseLeave={e => !asset.isBlocked && (e.currentTarget.style.background = 'transparent')}
                >
                  <div style={{ width: 28, height: 28, borderRadius: 'var(--radius-md)', background: asset.isBlocked ? '#FCEBEB' : '#E6F1FB', border: asset.isBlocked ? '1px solid #F09595' : 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <IconTruck size={14} color={asset.isBlocked ? '#A32D2D' : '#185FA5'} stroke={1.5} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, color: asset.isBlocked ? '#A32D2D' : 'var(--color-text-primary)', marginBottom: 2 }}>{asset.make} {asset.model}</div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 5 }}>
                      {asset.reg && <span style={{ fontFamily: 'monospace', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3 }}>{asset.reg}</span>}
                      {asset.type} {asset.isPool ? '· Pool' : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, background: asset.isBlocked ? 'var(--color-danger-light)' : 'var(--color-success-light)', color: asset.isBlocked ? 'var(--color-danger)' : 'var(--color-success)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3 }}>
                    {asset.isBlocked ? <><IconX size={10} stroke={2} /> Blocked</> : <><IconCheck size={10} stroke={2} /> Available</>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* Incidents */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '0.5px solid var(--color-border-light)' }}>
                <div style={{ fontSize: 14, fontWeight: 400 }}>Incidents</div>
                <span onClick={() => navigate('/incidents')} style={{ fontSize: 12, color: brandColour, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                  View all <IconArrowRight size={13} stroke={1.5} />
                </span>
              </div>
              {incidents.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', fontSize: 13, color: 'var(--color-text-tertiary)' }}>No incidents recorded</div>
              ) : incidents.map((inc, i) => {
                const colours = avatarColours[i % avatarColours.length];
                return (
                  <div key={inc.id} onClick={() => navigate(`/incidents/${inc.id}`)} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 18px', borderBottom: i < incidents.length - 1 ? '0.5px solid var(--color-border-light)' : 'none', cursor: 'pointer' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-bg-secondary)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: colours.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: colours.color, flexShrink: 0 }}>
                      {getInitials(inc.driverName)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 2 }}>{inc.driverName || '—'}</div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', marginBottom: 4 }}>
                        {inc.assetReg && <span style={{ fontFamily: 'monospace', fontSize: 10, background: '#FAC775', color: '#412402', padding: '1px 5px', borderRadius: 3, marginRight: 5 }}>{inc.assetReg}</span>}
                        {inc.location}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {inc.gforce && <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-text-secondary)' }}>{inc.gforce}G</span>}
                        <span style={{ fontSize: 10, padding: '1px 7px', borderRadius: 20, background: inc.severity === 'high' ? 'var(--color-danger-light)' : 'var(--color-warning-light)', color: inc.severity === 'high' ? 'var(--color-danger)' : 'var(--color-warning)' }}>
                          {inc.severity ? inc.severity.charAt(0).toUpperCase() + inc.severity.slice(1) : '—'}
                        </span>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', flexShrink: 0, whiteSpace: 'nowrap' }}>{formatTime(inc.detectedAt)}</div>
                  </div>
                );
              })}
            </div>

            {/* Fuel snapshot */}
            {mainTank && (
              <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 400 }}>{branchName} fuel tank</div>
                  <span onClick={() => navigate('/fuel')} style={{ fontSize: 12, color: brandColour, cursor: 'pointer' }}>View</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 32, fontWeight: 400, color: tankColour }}>{tankPct}%</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)' }}>{mainTank.currentLitres?.toLocaleString()} of {mainTank.capacityLitres?.toLocaleString()} L</div>
                </div>
                <div style={{ height: 6, background: 'var(--color-bg-secondary)', borderRadius: 3, overflow: 'hidden', marginBottom: 8 }}>
                  <div style={{ height: '100%', width: `${tankPct}%`, background: tankColour, borderRadius: 3 }} />
                </div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <IconDroplet size={11} stroke={1.5} />
                  ~{Math.round(mainTank.currentLitres / 120)} days remaining at current usage
                </div>
              </div>
            )}

            {/* Compliance */}
            <div style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 18px' }}>
              <div style={{ fontSize: 14, fontWeight: 400, marginBottom: 14 }}>Compliance snapshot</div>
              {[
                { label: 'Check completion', value: todayChecks.length > 0 ? Math.round((todayChecks.filter(c => c.result !== 'fail').length / todayChecks.length) * 100) : 0 },
                { label: 'Assets available', value: assets.length > 0 ? Math.round(((assets.length - blockedAssets.length) / assets.length) * 100) : 100 },
              ].map(item => {
                const colour = item.value >= 90 ? 'var(--color-success)' : item.value >= 75 ? 'var(--color-warning)' : 'var(--color-danger)';
                return (
                  <div key={item.label} style={{ marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{item.label}</span>
                      <span style={{ fontSize: 12, color: colour }}>{item.value}%</span>
                    </div>
                    <div style={{ height: 4, background: 'var(--color-bg-secondary)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${item.value}%`, background: colour, borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>

          </div>
        </div>
      </div>
    </Layout>
  );
}